import React, { useState, useRef, useEffect } from "react";
import Auth from "../../api/auth";
import {
  IonContent,
  IonPage,
  IonButton,
  IonText,
  IonCard,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonLoading,
  useIonToast,
} from "@ionic/react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { Redirect, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setTwoFAResolved, setIs2FA, setHasPermissions } from "./authSlice";
import TwoFAModal from "./TwoFAModal";
import InputItem from "../../components/formItems/InputItem";
import { ErrorMessage } from "@hookform/error-message";
import { getCurrentTenant } from "../../api/server";
import Loading from "./Loading";
const Login = () => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm({
    criteriaMode: "all",
  });
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const [present] = useIonToast();
  const { profile, twoFAResolved, is2FA, hasPermissions } = useSelector(
    (state) => state.auth
  );
  const [authError, setAuthError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [resolver, setResolver] = useState(null);
  const [verificationId, setVerificationId] = useState(null);
  const [twoFACode, setTwoFACode] = useState();
  const [twoFAError, setTwoFAError] = useState(null);
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [recaptcha, setRecaptcha] = useState(null);
  const [hasProvider, setHasProvider] = useState(null);
  const [providerTenant, setProviderTenant] = useState(false);
  const [providerName, setProviderName] = useState(false);
  const [isLoadingTenant, setIsLoadingTenant] = useState(false);
  const recaptchaRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    async function getTenantDetails() {
      const tenant = await getCurrentTenant();
      const {
        microsoft_auth_provider_enabled,
        microsoft_auth_provider_tenant,
      } = tenant;
      if (microsoft_auth_provider_enabled) {
        setHasProvider(true);
        setProviderTenant(microsoft_auth_provider_tenant);
        setProviderName("microsoft.com");
      } else {
        setHasProvider(false);
      }
    }
    async function run() {
      try {
        await getTenantDetails();
      } catch (e) {
        // console.error(e);
      } finally {
        setIsLoadingTenant(false);
      }
    }
    setIsLoadingTenant(true);
    run();
  }, []);

  useEffect(() => {
    if (inputRef.current && hasProvider === false) {
      setTimeout(() => inputRef.current.setFocus(), 900);
    }
  }, [inputRef]);

  useEffect(() => {
    const subscription = watch(() => {
      if (authError) {
        setAuthError(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, authError]);

  useEffect(() => {
    if (!hasPermissions) {
      present({
        color: "danger",
        duration: 2000,
        position: "top",
        message: intl.formatMessage({
          id: "app.mobile.notifications.no_mobile_access",
        }),
      });
      dispatch(setHasPermissions(true));
    }
  }, [hasPermissions]);

  useEffect(() => {
    if (
      profile &&
      (!profile.associated_employee_id || !profile.has_mobile_access)
    ) {
      present({
        color: "danger",
        duration: 2000,
        position: "top",
        message: intl.formatMessage({
          id: !profile.associated_employee_id
            ? `app.mobile.notifications.not_associated`
            : `app.mobile.notifications.no_mobile_access`,
        }),
      });
      setTimeout(() => {
        history.replace("/logout");
        return window.location.reload();
      }, 200);
    }
  }, [profile]);

  const onLoginWithProvider = async () => {
    setLoading(true);
    await Auth.signInWithProvider(providerName, providerTenant);
    setLoading(false);
  };

  const onFinish = async (values) => {
    const urlParams = new URLSearchParams(window.location.search);
    const system_user = urlParams.get("system_user");
    const isSystemUser = system_user === "true";
    setLoading(true);
    try {
      const result = await Auth.signInWithEmailAndPassword(
        values.username,
        values.password,
        isSystemUser,
        recaptchaRef.current,
        recaptcha
      );
      if (result.is2FA) {
        dispatch(setIs2FA(true));
        setResolver(result.resolver);
        setVerificationId(result.verificationId);
        setRecaptcha(result.recaptchaVerifier);
        setVisible(true);
      } else {
        dispatch(setIs2FA(false));
      }
      if (result.error) {
        setAutanticationError(result.error);
      }
    } catch (err) {
      setAutanticationError(err);
    } finally {
      setLoading(false);
    }
  };

  const setAutanticationError = (err) => {
    const message =
      err.code === "auth/wrong-password" ||
      err.code === "auth/invalid-email" ||
      err.code === "auth/invalid-action-code" ||
      err.code === "auth/too-many-requests" ||
      err.code === "auth/user-not-found"
        ? err.code
        : err.message;
    setAuthError(message);
  };
  const onCodeChange = (value) => {
    setTwoFACode(value);
  };

  const onConfirmCode = async () => {
    setTwoFALoading(true);
    Auth.confirm2FACode(resolver, verificationId, twoFACode)
      .then(() => {
        setVisible(false);
        dispatch(setTwoFAResolved(true));
        setTwoFALoading(false);
      })
      .catch((error) => {
        const message =
          error.code === "auth/too-many-requests" ||
          error.code === "auth/invalid-verification-code"
            ? error.code
            : error.message;
        setTwoFAError(message);
        setTwoFALoading(false);
      });
  };

  const onResend = async () => {
    setResendLoading(true);
    try {
      const result = await Auth.resend2FACode(resolver, recaptcha);
      setResendLoading(false);
      setVerificationId(result.verificationId);
      setTwoFAError(null);
    } catch (err) {
      const message =
        err.code === "auth/too-many-requests" ? err.code : err.message;
      setTwoFAError(message);
    } finally {
      setResendLoading(false);
    }
  };

  const handleCancel = () => {
    setVisible(false);
    setAuthError(null);
    setVerificationId(null);
    setTwoFACode(null);
    setTwoFAError(null);
    Auth.signOut();
  };

  const emailReg =
    /^([-!#-'*+/-9=?A-Z^-~]+(\.[-!#-'*+/-9=?A-Z^-~]+)*|"([]!#-[^-~ \t]|(\\[\t -~]))+")@[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?(\.[0-9A-Za-z]([0-9A-Za-z-]{0,61}[0-9A-Za-z])?)+$/;
  if (
    profile &&
    (twoFAResolved || is2FA === false || hasProvider) &&
    profile.associated_employee_id
  ) {
    return <Redirect to="" />;
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            {intl.formatMessage({
              id: "app.mobile.login",
            })}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      {isLoadingTenant ? (
        <Loading />
      ) : hasProvider ? (
        <IonContent>
          <IonCard>
            <div style={{ padding: "16px 16px 0 16px" }}>
              <IonText color="muted">
                <h2>
                  {intl.formatMessage({
                    id: "app.mobile.forms.user.login_title",
                  })}
                </h2>
                <p>
                  {intl.formatMessage({
                    id: "app.mobile.forms.user.login_subtitle",
                  })}
                </p>
              </IonText>
              <div className="ion-padding">
                <IonButton
                  expand="block"
                  className="ion-margin-top"
                  onClick={onLoginWithProvider}
                >
                  {intl.formatMessage({
                    id: "app.mobile.actions.connect",
                  })}
                </IonButton>
              </div>
            </div>
          </IonCard>
        </IonContent>
      ) : (
        <IonContent>
          <IonCard>
            <div style={{ padding: "16px 16px 0 16px" }}>
              <IonText color="muted">
                <h2>
                  {intl.formatMessage({
                    id: "app.mobile.forms.user.login_title",
                  })}
                </h2>
                <p>
                  {intl.formatMessage({
                    id: "app.mobile.forms.user.login_subtitle",
                  })}
                </p>
              </IonText>
            </div>
            <form onSubmit={handleSubmit(onFinish)}>
              <InputItem
                name="username"
                control={control}
                label={intl.formatMessage({
                  id: "app.mobile.forms.user.username",
                })}
                rules={{
                  required: `${intl.formatMessage({
                    id: "app.mobile.forms.user.username",
                  })} ${intl.formatMessage({
                    id: "app.mobile.forms.requiredMessage",
                  })}`,
                  pattern: {
                    value: emailReg,
                    message: intl.formatMessage({
                      id: "app.mobile.forms.emailMessage",
                    }),
                  },
                }}
                inputRef={inputRef}
              />
              <div className="errors_container">
                <ErrorMessage
                  errors={errors}
                  name="username"
                  render={({ messages }) =>
                    messages &&
                    Object.entries(messages).map(([type, message]) => (
                      <p key={type}>{message}</p>
                    ))
                  }
                />
              </div>

              <InputItem
                name="password"
                type="password"
                control={control}
                label={intl.formatMessage({
                  id: "app.mobile.forms.user.password",
                })}
                rules={{
                  required: `${intl.formatMessage({
                    id: "app.mobile.forms.user.password",
                  })} ${intl.formatMessage({
                    id: "app.mobile.forms.requiredMessage",
                  })}`,
                }}
              />
              <div className="errors_container">
                <ErrorMessage
                  errors={errors}
                  name="password"
                  render={({ messages }) => {
                    return (
                      messages &&
                      Object.entries(messages).map(([type, message]) => (
                        <p key={type}>{message}</p>
                      ))
                    );
                  }}
                />
              </div>

              <IonText
                color="muted"
                style={{
                  padding: "20px",
                  display: "flex",
                  position: "absolute",
                  bottom: "100px",
                }}
              >
                {authError ? (
                  <p
                    style={{
                      margin: "-20px 0 20px ",
                      width: "300px",
                      color: "red",
                    }}
                  >
                    {intl.formatMessage({
                      id: `app.mobile.notifications.${authError}`,
                      defaultMessage: authError,
                    })}
                  </p>
                ) : null}
              </IonText>
              <div className="ion-padding">
                <IonButton
                  expand="block"
                  className="ion-margin-top"
                  type="submit"
                >
                  {intl.formatMessage({
                    id: "app.mobile.actions.login",
                  })}
                </IonButton>
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <IonButton routerLink={`/forgot`} fill="clear">
                    {intl.formatMessage({
                      id: "app.mobile.actions.forgot_password",
                    })}
                  </IonButton>
                </div>
              </div>
              <div ref={recaptchaRef} id="sign-in-button" />
            </form>
          </IonCard>
          <IonLoading isOpen={loading} onDidDismiss={() => setLoading(false)} />
        </IonContent>
      )}
      <TwoFAModal
        visible={visible}
        handleCancel={handleCancel}
        phoneNumber={resolver && resolver.hints[0].phoneNumber}
        onCodeChange={onCodeChange}
        onSubmitCode={onConfirmCode}
        onResend={onResend}
        twoFAError={twoFAError}
        twoFACode={twoFACode}
        loading={twoFALoading}
        resendLoading={resendLoading}
      />
    </IonPage>
  );
};

export default Login;
